import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import DescriptionList from '../components/shared/DescriptionList'
import AircraftOptions from '../components/aircraft/AircraftOptions'
import AircraftNotes from '../components/notes/AircraftNotes'
import AircraftCompanies from '../components/aircraft/AircraftCompanies'
import OverrideDataModal from '../components/overrides/OverrideDataModal'
import { TransactionsService } from '../services/intellijetnet/transactions.service'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { OffMarketService } from '../services/off-market.service'
import { UnifiedAircraftService } from '../services/unified-aircraft.service'
import { OverridesService } from '../services/overrides.service'
import { Transaction, Aircraft } from '../types/intellijetnet'
import { AircraftOverride, OffMarket, AircraftTransaction } from '../types/database'
import { AircraftTransactionsService } from '../services/aircraft-transactions.service'
import { TransactionOverridesNewService } from '../services/transaction-overrides-new.service'
import { capitalizeMakeName, formatModelName, formatPrice, formatDate } from '../utils/formatters'

export default function TransactionDetail() {
  const { modelId, transId } = useParams<{ modelId: string; transId: string }>()
  const navigate = useNavigate()
  const [transaction, setTransaction] = useState<Transaction | null>(null)
  const [aircraft, setAircraft] = useState<Aircraft | null>(null)
  const [override, setOverride] = useState<AircraftOverride | null>(null)
  const [offMarket, setOffMarket] = useState<OffMarket | null>(null)
  const [modelInfo, setModelInfo] = useState<{
    model_id: number
    make_name: string
    model_name: string
    display_name: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [aircraftTransaction, setAircraftTransaction] = useState<AircraftTransaction | null>(null)
  const [effectiveTransaction, setEffectiveTransaction] = useState<any>(null)

  useEffect(() => {
    if (modelId && transId) {
      fetchData()
    }
    // Always scroll to top when component mounts
    window.scrollTo(0, 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, transId])

  // Calculate effective transaction data when transaction or snapshot changes
  useEffect(() => {
    if (transaction) {
      calculateEffectiveTransaction()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, aircraftTransaction])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const numericModelId = parseInt(modelId!, 10)
      const numericTransId = parseInt(transId!, 10)
      
      if (isNaN(numericModelId) || isNaN(numericTransId)) {
        throw new Error('Invalid model or transaction ID')
      }

      // Fetch model info
      const info = await ModelInfoCacheService.getModelInfo(numericModelId)
      if (!info) {
        throw new Error('Model not found')
      }
      setModelInfo(info)

      // Fetch transaction details
      const transactionData = await TransactionsService.getById(numericTransId)
      if (!transactionData) {
        throw new Error('Transaction not found')
      }
      setTransaction(transactionData)

      // Fetch aircraft details if available
      if (transactionData.aircraft_id) {
        const aircraftData = await AircraftService.getById(transactionData.aircraft_id)
        setAircraft(aircraftData)

        // Fetch override data for the aircraft
        const overrideData = await OverridesService.getByAircraftId(transactionData.aircraft_id)
        setOverride(overrideData)

        // Check if aircraft is in off-market
        try {
          const offMarketData = await OffMarketService.getByAircraftId(transactionData.aircraft_id)
          setOffMarket(offMarketData.length > 0 ? offMarketData[0] : null)
        } catch (err) {
          // Aircraft not in off-market, which is fine
          setOffMarket(null)
        }
      }

      // Check if we have a snapshot for this API transaction
      if (transactionData.trans_id) {
        try {
          const snapshot = await AircraftTransactionsService.getByApiTransactionId(transactionData.trans_id)
          setAircraftTransaction(snapshot)
        } catch (err) {
          // No snapshot found - not all API transactions have snapshots
          setAircraftTransaction(null)
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const formatBoolean = (value: boolean | null): string => {
    if (value === null) return '-'
    return value ? 'Yes' : 'No'
  }

  const calculateEffectiveTransaction = async () => {
    if (!aircraftTransaction) {
      // No snapshot, use original API transaction data
      setEffectiveTransaction(transaction)
      return
    }

    try {
      // Get transaction overrides for the snapshot
      const transactionOverride = await TransactionOverridesNewService.getByTransactionId(aircraftTransaction.transaction_id)

      // Merge override data with snapshot data
      const effective = TransactionOverridesNewService.getEffectiveTransactionData(aircraftTransaction, transactionOverride)
      setEffectiveTransaction(effective)
    } catch (err) {
      // Fall back to snapshot data
      setEffectiveTransaction(aircraftTransaction)
    }
  }

  const handleOverrideSave = async (savedOverride: AircraftOverride | null) => {
    setOverride(savedOverride)

    // If we have a transaction snapshot, refresh it to get updated override data
    if (aircraftTransaction && transaction) {
      try {
        const updatedSnapshot = await AircraftTransactionsService.getByApiTransactionId(transaction.trans_id)
        setAircraftTransaction(updatedSnapshot)
        // The useEffect will automatically recalculate effective data
      } catch (err) {
        // Error refreshing transaction snapshot
      }
    }

    setShowOverrideModal(false)
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">Loading transaction information...</div>
        </div>
      </Layout>
    )
  }

  if (error || !transaction || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">{error || 'Transaction not found'}</div>
            <button
              onClick={() => navigate(`/market/${modelId}`)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Market
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
          <div className="w-full max-w-full">
            <div className="mb-6">
              <button
                onClick={() => navigate(`/market/${modelId}`)}
                className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
              >
                ← Back to Market
              </button>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <h1 className="text-3xl font-bold text-purple-950">
                    {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)} - Transaction #{transaction.trans_id}
                  </h1>
                  {offMarket && (
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">Category:</span>
                        <select
                          value={offMarket.market_category}
                          onChange={async (e) => {
                            try {
                              const newCategory = e.target.value as 'for_sale' | 'recently_sold' | 'removed_from_market'
                              if (aircraft && offMarket) {
                                await OffMarketService.updateCategory(aircraft.aircraft_id.toString(), newCategory)
                                // Refresh the page to show the updated status
                                window.location.reload()
                              }
                            } catch (error) {
                              alert('Failed to update category. Please try again.')
                            }
                          }}
                          className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="for_sale">For Sale</option>
                          <option value="recently_sold">Recently Sold</option>
                          <option value="removed_from_market">Removed From Market</option>
                        </select>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            if (aircraft) {
                              await UnifiedAircraftService.removeFromOffMarket(aircraft.aircraft_id)
                              // Navigate back to market page
                              window.location.href = `/market/${modelId}`
                            }
                          } catch (error) {
                            alert('Failed to remove from market. Please try again.')
                          }
                        }}
                        className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded-md transition-colors duration-150"
                        title="Remove from market summary"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>Remove from Summary</span>
                      </button>
                    </div>
                  )}

                </div>
                {aircraft && (
                  <button
                    onClick={() => setShowOverrideModal(true)}
                    className="bg-purple-950 text-white hover:bg-purple-900 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150"
                  >
                    Override Data
                  </button>
                )}
              </div>
            </div>

          {/* Full-width information sections */}
          <div className="space-y-8">
            {/* Notes */}
            {transaction.aircraft_id && (
              <AircraftNotes
                aircraftId={transaction.aircraft_id}
                modelId={transaction.model_id || parseInt(modelId!, 10)}
              />
            )}

            <div className="bg-white shadow-sm rounded-lg p-6">
              <DescriptionList columns={2} sections={[
                {
                  title: 'Transaction Information',
                  subtitle: 'Transaction details and timeline',
                  items: [
                    { label: 'Transaction ID', value: transaction.trans_id },
                    { label: 'Transaction Type', value: transaction.trans_type },
                    { label: 'Transaction Date', value: transaction.trans_date, formatter: formatDate },
                    { label: 'Action Date', value: transaction.action_date, formatter: formatDate },
                    { label: 'Last Action Date', value: transaction.trans_last_action_date, formatter: formatDate },
                  ]
                },
                {
                  title: 'Pricing Details',
                  subtitle: 'Transaction pricing and market information',
                  items: [
                    { label: 'Sold Price', value: effectiveTransaction?.sale_price ?? transaction.sold_price, formatter: (v) => v !== null ? formatPrice(v.toString()) : '-' },
                    { label: 'Asking Price', value: effectiveTransaction?.asking_price ?? transaction.asking_price, formatter: (v) => v !== null ? formatPrice(v.toString()) : '-' },
                    { label: 'List Date', value: transaction.list_date, formatter: formatDate },
                    { label: 'Purchase Date', value: transaction.purchase_date, formatter: formatDate },
                    { label: 'For Sale', value: transaction.for_sale, formatter: formatBoolean },
                    { label: 'Transaction Retail', value: transaction.trans_retail, formatter: formatBoolean },
                  ]
                },
                {
                  title: 'Aircraft Details',
                  subtitle: 'Aircraft identification and specifications',
                  items: [
                    { label: 'Aircraft ID', value: transaction.aircraft_id },
                    ...(aircraft ? [
                      { label: 'Serial Number', value: aircraft.sernbr },
                      { label: 'Registration', value: aircraft.regnbr },
                    ] : []),
                    { label: 'Model ID', value: transaction.model_id },
                    { label: 'New Aircraft', value: transaction.new_ac },
                    { label: 'Usage', value: transaction.usage },
                  ]
                },
                {
                  title: 'Technical Details',
                  subtitle: 'Aircraft technical information',
                  items: [
                    { label: 'Est. AFTT', value: effectiveTransaction?.aftt ?? transaction.est_aftt, formatter: (v) => v !== null ? v.toLocaleString() : '-' },
                    { label: 'Est. Cycles', value: transaction.est_cycles, formatter: (v) => v !== null ? v.toLocaleString() : '-' },
                    { label: 'APU TTS New', value: transaction.aputts_new, formatter: (v) => v !== null ? v.toLocaleString() : '-' },
                    { label: 'Lifecycle', value: transaction.lifecycle },
                  ]
                },
                {
                  title: 'Base Location',
                  subtitle: 'Aircraft base location information',
                  items: [
                    { label: 'Base IATA', value: transaction.base_iata },
                    { label: 'Base ICAO', value: transaction.base_icao },
                    { label: 'Base Name', value: transaction.base_name },
                    { label: 'Base State', value: transaction.base_state },
                    { label: 'Base Country', value: transaction.base_country },
                    { label: 'Base Continent', value: transaction.base_continent },
                  ]
                },
                {
                  title: 'Additional Details',
                  subtitle: 'Other transaction information',
                  items: [
                    { label: 'Description', value: transaction.description },
                    { label: 'Internal Transaction', value: transaction.internal_trans, formatter: formatBoolean },
                    { label: 'Market Status', value: transaction.market_status },
                    ...(transaction.page_url ? [{
                      label: 'JetNet Record',
                      value: (
                        <a
                          href={transaction.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-purple-950 text-white text-sm font-medium rounded-md hover:bg-purple-900 transition-colors duration-150"
                        >
                          View Record
                          <svg className="ml-2 -mr-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </a>
                      )
                    }] : []),
                  ]
                },
                {
                  title: 'System Information',
                  subtitle: 'Database and sync information',
                  items: [
                    { label: 'Active', value: transaction.is_active, formatter: formatBoolean },
                    { label: 'Last Modified', value: transaction.jetnet_last_modified, formatter: formatDate },
                    { label: 'Created At', value: transaction.created_at, formatter: formatDate },
                    { label: 'Updated At', value: transaction.updated_at, formatter: formatDate },
                  ]
                },
              ]} />
            </div>
          </div>

          {/* Editable sections at the bottom */}
          {transaction.aircraft_id && (
            <div className="mt-8 space-y-8">
              <AircraftOptions
                aircraftId={transaction.aircraft_id}
                modelId={transaction.model_id || parseInt(modelId!, 10)}
                readonly={false}
              />
              <AircraftCompanies
                aircraftId={transaction.aircraft_id}
                override={override}
              />
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Override Data Modal */}
      {aircraft && (
        <OverrideDataModal
          isOpen={showOverrideModal}
          onClose={() => setShowOverrideModal(false)}
          aircraft={aircraft}
          onSave={handleOverrideSave}
          containerType={aircraftTransaction ? "sold" : undefined}
          transaction={aircraftTransaction || undefined}
        />
      )}
    </Layout>
  )
}